/**
 * Os trabalhadores que este agente cadastrou.
 *
 * Os devolvidos vêm primeiro e com o motivo à vista: é o único trabalho da
 * lista que só o agente pode desbloquear, e é para isso que ele abre este
 * ecrã.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Loader2, ChevronDown, MapPin, ClipboardCheck, GraduationCap, Phone } from 'lucide-react';
import api from '../../utils/api';

const FASES = {
  pending:  { txt: 'Candidato',            cls: 'bg-slate-700 text-slate-300' },
  verified: { txt: 'Documentos validados', cls: 'bg-blue-500/15 text-blue-400' },
  approved: { txt: 'Entrevista aprovada',  cls: 'bg-purple-500/15 text-purple-400' },
  trained:  { txt: 'Formação concluída',   cls: 'bg-cyan-500/15 text-cyan-400' },
  active:   { txt: 'Activo',               cls: 'bg-green-500/15 text-green-400' },
  returned: { txt: 'Devolvido',            cls: 'bg-amber-500/15 text-amber-400' },
  rejected: { txt: 'Rejeitado',            cls: 'bg-red-500/15 text-red-400' },
};

const data = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '—');

const FieldWorkers = () => {
  const navigate = useNavigate();
  const [lista, setLista] = useState(null);
  const [aberto, setAberto] = useState(null);
  const [percurso, setPercurso] = useState({});
  const [porFormar, setPorFormar] = useState([]);
  const [aAdoptar, setAAdoptar] = useState(null);
  const [errosAdopcao, setErrosAdopcao] = useState({});

  const carregar = useCallback(() => {
    api.get('/operations/my-workers')
      .then((r) => setLista(r.data || []))
      .catch(() => setLista([]));
    api.get('/operations/workers/por-formar')
      .then((r) => setPorFormar(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPorFormar([]));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const adoptar = async (id) => {
    setAAdoptar(id);
    setErrosAdopcao((prev) => ({ ...prev, [id]: '' }));
    try {
      await api.post(`/operations/workers/${id}/adopt`);
      carregar();
    } catch (e) {
      carregar();
      setErrosAdopcao((prev) => ({
        ...prev,
        [id]: e.response?.data?.message || 'Não foi possível ficar com este profissional.',
      }));
    } finally {
      setAAdoptar(null);
    }
  };

  const abrir = async (w) => {
    const id = w.user_id;
    setAberto(aberto === id ? null : id);
    if (!percurso[id]) {
      try {
        const { data: h } = await api.get(`/operations/workers/${id}/pipeline`);
        setPercurso((p) => ({ ...p, [id]: Array.isArray(h) ? h : [] }));
      } catch { setPercurso((p) => ({ ...p, [id]: [] })); }
    }
  };

  // Devolvidos ao cimo — é o que exige acção do agente.
  const ordenada = [...(lista || [])].sort((a, b) => {
    const peso = (w) => (w.pipeline_status === 'returned' ? 0 : 1);
    return peso(a) - peso(b);
  });

  return (
    <div className="space-y-4">
      {porFormar.length > 0 && (
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-3 space-y-2">
          <h2 className="text-sm font-semibold text-slate-200">Registados pela app, sem agente</h2>
          <p className="text-xs text-slate-400">
            Registaram-se na app e ainda não têm agente. Só aparecem as zonas que cobre.
          </p>
          <ul className="space-y-2">
            {porFormar.map((w) => {
              const f = FASES[w.pipeline_status] || FASES.pending;
              return (
                <li key={w.user_id} className="rounded-lg border border-slate-800 p-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate max-[390px]:whitespace-normal max-[390px]:line-clamp-2">{w.full_name}</div>
                      <div className="text-xs text-slate-400 font-mono">{w.worker_code || 'Sem código'}</div>
                      {w.profession && (
                        <div className="text-xs text-slate-400 mt-1">{w.profession}</div>
                      )}
                      {w.phone_number && (
                        <a href={`tel:${String(w.phone_number).replace(/\s/g, '')}`}
                          className="text-xs text-slate-400 inline-flex items-center gap-1 mt-1 min-h-[44px]">
                          <Phone className="w-3 h-3" />{w.phone_number}
                        </a>
                      )}
                      {w.zona_nome && (
                        <div className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />{w.zona_nome}
                        </div>
                      )}
                      <div className="text-xs text-slate-400 mt-1">Registo {data(w.created_at)}</div>
                    </div>
                    <span className={`shrink-0 px-2 py-1 rounded-md text-[11px] font-semibold ${f.cls}`}>
                      {f.txt}
                    </span>
                  </div>
                  {errosAdopcao[w.user_id] && (
                    <p className="mt-2 text-sm text-red-300">{errosAdopcao[w.user_id]}</p>
                  )}
                  <button
                    type="button"
                    disabled={aAdoptar === w.user_id}
                    onClick={() => adoptar(w.user_id)}
                    className="mt-3 w-full min-h-[44px] rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-60"
                  >
                    {aAdoptar === w.user_id ? 'A atribuir…' : 'Ficar com este profissional'}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="flex items-center gap-3">
        <h1 className="text-xl font-bold flex-1">Os meus profissionais</h1>
        <button
          onClick={() => navigate('/admin/workers/new')}
          aria-label="Novo profissional"
          className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600"
        >
          <UserPlus className="w-5 h-5" />
        </button>
      </div>

      {lista === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>
      ) : ordenada.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <UserPlus className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium text-slate-400">Ainda não cadastrou ninguém</p>
          <p className="text-sm mt-1">Toque no + para registar o primeiro.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {ordenada.map((w) => {
            const f = FASES[w.pipeline_status] || FASES.pending;
            const devolvido = w.pipeline_status === 'returned';
            const motivo = (percurso[w.user_id] || []).slice().reverse()
              .find((h) => h.para_fase === 'returned')?.motivo;
            return (
              <li key={w.user_id}
                className={`rounded-xl border ${devolvido ? 'border-amber-500/40 bg-amber-500/5' : 'border-slate-800 bg-slate-900'}`}>
                <button onClick={() => abrir(w)} className="w-full text-left p-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{w.full_name}</div>
                      <div className="text-xs text-slate-400 font-mono">{w.worker_code}</div>
                    </div>
                    <span className={`shrink-0 px-2 py-1 rounded-md text-[11px] font-semibold ${f.cls}`}>
                      {f.txt}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
                    {w.profession && <span>{w.profession}</span>}
                    {w.zona_codigo && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />{w.zona_nome}
                      </span>
                    )}
                    <span className="ml-auto flex items-center gap-1">
                      {data(w.recruited_at)}
                      <ChevronDown className={`w-4 h-4 transition-transform ${aberto === w.user_id ? 'rotate-180' : ''}`} />
                    </span>
                  </div>
                </button>

                {aberto === w.user_id && (
                  <div className="px-3 pb-3 border-t border-slate-800 pt-3">
                    {devolvido && motivo && (
                      <div className="p-2.5 mb-3 rounded-lg bg-amber-500/10 text-amber-200 text-sm">
                        <div className="font-semibold text-amber-300 text-xs uppercase tracking-wide mb-0.5">
                          Motivo da devolução
                        </div>
                        {motivo}
                      </div>
                    )}
                    {/* O passo seguinte do percurso, à mão. Sem isto o agente
                        sabe em que fase está mas não o que fazer a seguir. */}
                    {w.pipeline_status === 'verified' && (
                      <button onClick={() => navigate(`/admin/workers/${w.user_id}/interview`)}
                        className="w-full mb-3 min-h-[44px] rounded-lg bg-blue-600 text-white font-semibold
                                   inline-flex items-center justify-center gap-2">
                        <ClipboardCheck className="w-4 h-4" /> Fazer entrevista
                      </button>
                    )}
                    {['approved', 'trained'].includes(w.pipeline_status) && (
                      <button onClick={() => navigate(`/admin/workers/${w.user_id}/training`)}
                        className="w-full mb-3 min-h-[44px] rounded-lg bg-slate-800 text-slate-100 font-semibold
                                   inline-flex items-center justify-center gap-2">
                        <GraduationCap className="w-4 h-4" /> Formação
                      </button>
                    )}

                    <ol className="space-y-1.5 text-xs">
                      {(percurso[w.user_id] || []).map((h, i) => (
                        <li key={i} className="flex gap-2 text-slate-400">
                          <span className="text-slate-600 tabular-nums shrink-0">
                            {new Date(h.created_at).toLocaleDateString('pt-PT')}
                          </span>
                          <span>{FASES[h.para_fase]?.txt || h.para_fase}</span>
                          {h.actor_nome && <span className="text-slate-600">· {h.actor_nome}</span>}
                        </li>
                      ))}
                      {(percurso[w.user_id] || []).length === 0 && (
                        <li className="text-slate-600">Sem movimentos registados.</li>
                      )}
                    </ol>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FieldWorkers;
